import { useMemo } from "react";
import { createTheme } from "@mui/material";
import { useThemeStore } from "./useTheme";

export const useProjectTheme = () => {
  const { mode } = useThemeStore();

  const theme = useMemo(
    () =>
      createTheme({
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                ":disabled": {
                  color:
                    mode === "light"
                      ? "rgba(0, 0, 0, 0.26)"
                      : "rgba(255,255,255,.3)",
                },
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                color: mode === "light" ? "#242531" : "#fff",
                borderBottom: "none",
                padding: "clamp(6px, 0.75vw, 6px) clamp(0.25rem, 1vw, 6px)",
                borderTop:
                  mode === "light"
                    ? "1px solid rgba(0,0,0,0.05)"
                    : "1px solid #585b627a",
                "@media (max-width: 1200px)": {
                  fontSize: "12px",
                },
                fontWeight: 500,
              },
            },
          },
          MuiInput: {
            styleOverrides: {
              input: {
                color: mode === "light" ? "#000" : "#fff",
                "&::placeholder": {
                  color: "#ABB0C1",
                  "@media (max-width: 600px)": {
                    fontSize: "12px",
                  },
                },
              },
              root: {
                height: "48px",
                backgroundColor: "#F1F2F5",
                padding: "0 18px",
                borderRadius: 12,
                "@media (max-width: 600px)": {
                  borderRadius: 8,
                },
                color: "#ABB0C1",
                fontSize: "18px",
                fontWeight: 600,
                ":after": {
                  display: "none",
                },
                ":before": {
                  display: "none",
                },
              },
            },
          },
          MuiSwitch: {
            styleOverrides: {
              switchBase: {
                color: "#fff",
                position: "absolute",
                left: "-5px",
                top: "-6px",
                "@media (max-width: 1200px)": {
                  left: 0,
                  top: 0,
                },
                "&.Mui-checked": {
                  color: "#fff",
                  transform: "translateX(20px)",
                  "@media (max-width: 1200px)": {
                    transform: "translateX(10px)",
                    left: "0px",
                    top: 0,
                  },
                },
              },
              track: {
                display: "none",
              },
              thumb: {
                width: "20px",
                height: "20px",
                "@media (max-width: 1200px)": {
                  width: "12px",
                  height: "12px",
                  position: "absolute",
                  top: "2px",
                  left: "3px",
                },
              },
              root: {
                backgroundColor: "#9DA1B2",
                height: "26px",
                width: "48px",
                "@media (max-width: 1200px)": {
                  width: "28px",
                  height: "16px",
                  padding: 0,
                },
                borderRadius: "100px",
                boxShadow: "0px 0px 6px #00000029",
                "&.Mui-checked": {
                  display: "none",
                },
              },
            },
          },
          MuiDialog: {
            defaultProps: {},
            styleOverrides: {
              container: {
                padding: "0 10px",
                "@media (max-width: 1200px)": {
                  padding: "0 20px",
                },
                or: "#DDE0FD",
              },
            },
          },
          MuiTooltip: {
            defaultProps: {
              placement: "top-start",
              arrow: true,
              enterDelay: 300,
            },
            styleOverrides: {
              arrow: {
                color: "#DDE0FD",
              },
              tooltip: {
                backgroundColor: "#DDE0FD",
                color: "#5D6880",
                fontWeight: 500,
                fontSize: "10px",
                borderRadius: "2px",
              },
            },
          },
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
            laptop: 1440,
          },
        },
        typography: {
          fontFamily: '"Poppins", sans-serif',
          fontWeightRegular: 400,
          fontWeightMedium: 500,
        },
        palette: {
          ...(mode === "dark"
            ? {
                primary: {
                  main: "#5763F3",
                  light: "#22246A",
                  placeholder: "#ffffff",
                },
                secondary: {
                  main: "#151447",
                  dark: "#171535",
                },
                background: {
                  default: "#080628",
                  paper: "#151447",
                },
                text: {
                  primary: "#fff",
                  secondary: "#fff",
                  disabled: "#ABB0C1",
                },
                divider: "#838A9B",
              }
            : {
                primary: {
                  main: "#5763F3",
                  light: "#F1F2F5",
                  placeholder: "#5b606c",
                },
                secondary: {
                  main: "#F1F2F5",
                  dark: "#F1F2F5",
                },
                background: {
                  default: "#fff",
                  paper: "#F8F9FB",
                },
                text: {
                  primary: "#242531",
                  secondary: "#202020",
                  disabled: "#242531",
                },
                divider: "#838A9B",
              }),
        },
      }),
    [mode]
  );

  return [theme];
};
